import React, { useState } from 'react';
import { SearchIcon, PackageIcon, TruckIcon, XIcon, CheckIcon, FileTextIcon, ImageIcon, AlertCircleIcon, ChevronRightIcon } from 'lucide-react';
import { Button } from './ui/Button';
import { Input } from './ui/Input';
import { DraggableModal } from './ui/DraggableModal';
import { formatAmount } from '../utils/currencyFormat';
import { useKeyboardShortcuts } from '../hooks/useKeyboardShortcuts';
interface POMatchingModalProps {
  vendorName: string;
  invoiceTotal: number;
  invoiceCurrency: string;
  onApply: (poNumber: string, selectedLines: string[], matchingType: '2-way' | '3-way') => void;
  onClose: () => void;
}
// Mock data structures
interface POLineItem {
  id: string;
  lineNumber: number;
  itemCode: string;
  description: string;
  quantity: number;
  unitPrice: number;
  total: number;
  receivedQty: number;
  invoicedQty: number;
  remainingQty: number;
}
interface GoodsReceipt {
  id: string;
  grNumber: string;
  grDate: string;
  lineNumber: number;
  quantity: number;
  status: 'Posted' | 'Pending';
}
interface PurchaseOrder {
  poNumber: string;
  poDate: string;
  supplier: string;
  totalAmount: number;
  currency: string;
  status: 'Open' | 'Partially Received' | 'Fully Received';
  lineItems: POLineItem[];
  goodsReceipts: GoodsReceipt[];
}
// Mock PO data
const MOCK_POS: PurchaseOrder[] = [{
  poNumber: 'PO00789',
  poDate: '2024-01-02',
  supplier: 'Dell Australia',
  totalAmount: 109090.91,
  currency: 'AUD',
  status: 'Partially Received',
  lineItems: [{
    id: 'line-1',
    lineNumber: 10,
    itemCode: 'LAT-5420-001',
    description: 'Dell Latitude 5420 Laptop - 14" FHD, i5-1145G7, 16GB RAM, 256GB SSD',
    quantity: 100,
    unitPrice: 1090.91,
    total: 109090.91,
    receivedQty: 80,
    invoicedQty: 80,
    remainingQty: 20
  }, {
    id: 'line-2',
    lineNumber: 20,
    itemCode: 'MON-27-001',
    description: 'Dell 27" UltraSharp Monitor - 4K, USB-C, Height Adjustable',
    quantity: 50,
    unitPrice: 545.45,
    total: 27272.73,
    receivedQty: 50,
    invoicedQty: 0,
    remainingQty: 50
  }, {
    id: 'line-3',
    lineNumber: 30,
    itemCode: 'KB-MOUSE-001',
    description: 'Dell Wireless Keyboard and Mouse Combo',
    quantity: 100,
    unitPrice: 90.91,
    total: 9090.91,
    receivedQty: 100,
    invoicedQty: 0,
    remainingQty: 100
  }],
  goodsReceipts: [{
    id: 'gr-1',
    grNumber: 'GR-2024-0123',
    grDate: '2024-01-05',
    lineNumber: 10,
    quantity: 30,
    status: 'Posted'
  }, {
    id: 'gr-2',
    grNumber: 'GR-2024-0145',
    grDate: '2024-01-12',
    lineNumber: 10,
    quantity: 50,
    status: 'Posted'
  }, {
    id: 'gr-3',
    grNumber: 'GR-2024-0156',
    grDate: '2024-01-15',
    lineNumber: 20,
    quantity: 50,
    status: 'Posted'
  }, {
    id: 'gr-4',
    grNumber: 'GR-2024-0167',
    grDate: '2024-01-18',
    lineNumber: 30,
    quantity: 100,
    status: 'Posted'
  }]
}, {
  poNumber: 'PO00856',
  poDate: '2024-01-15',
  supplier: 'Office Furniture Co',
  totalAmount: 20000.0,
  currency: 'AUD',
  status: 'Fully Received',
  lineItems: [{
    id: 'line-1',
    lineNumber: 10,
    itemCode: 'CHAIR-ERG-001',
    description: 'Ergonomic Mesh Office Chair - Adjustable Arms, Lumbar Support',
    quantity: 25,
    unitPrice: 327.27,
    total: 9000.0,
    receivedQty: 25,
    invoicedQty: 25,
    remainingQty: 0
  }, {
    id: 'line-2',
    lineNumber: 20,
    itemCode: 'DESK-STD-001',
    description: 'Standing Desk - Electric Height Adjustable, 1600mm x 800mm',
    quantity: 15,
    unitPrice: 363.64,
    total: 6000.0,
    receivedQty: 15,
    invoicedQty: 15,
    remainingQty: 0
  }, {
    id: 'line-3',
    lineNumber: 30,
    itemCode: 'CABINET-FILE-001',
    description: 'Mobile Filing Cabinet - 3 Drawer, Lockable',
    quantity: 20,
    unitPrice: 227.27,
    total: 5000.0,
    receivedQty: 20,
    invoicedQty: 20,
    remainingQty: 0
  }],
  goodsReceipts: [{
    id: 'gr-3',
    grNumber: 'GR-2024-0189',
    grDate: '2024-01-22',
    lineNumber: 10,
    quantity: 25,
    status: 'Posted'
  }, {
    id: 'gr-4',
    grNumber: 'GR-2024-0189',
    grDate: '2024-01-22',
    lineNumber: 20,
    quantity: 15,
    status: 'Posted'
  }, {
    id: 'gr-5',
    grNumber: 'GR-2024-0190',
    grDate: '2024-01-23',
    lineNumber: 30,
    quantity: 20,
    status: 'Posted'
  }]
}, {
  poNumber: 'PO00912',
  poDate: '2024-01-20',
  supplier: 'Office Furniture Co',
  totalAmount: 8000.0,
  currency: 'AUD',
  status: 'Open',
  lineItems: [{
    id: 'line-1',
    lineNumber: 10,
    itemCode: 'DESK-STD-001',
    description: 'Standard Office Desk - 1400mm x 700mm',
    quantity: 20,
    unitPrice: 363.64,
    total: 8000.0,
    receivedQty: 0,
    invoicedQty: 0,
    remainingQty: 20
  }],
  goodsReceipts: []
}, {
  poNumber: 'PO00789',
  poDate: '2024-01-02',
  supplier: 'Abbott Australasia Pty Ltd',
  totalAmount: 120000.0,
  currency: 'AUD',
  status: 'Partially Received',
  lineItems: [{
    id: 'line-1',
    lineNumber: 10,
    itemCode: 'MED-001',
    description: 'Medical Equipment - Diagnostic Scanner',
    quantity: 2,
    unitPrice: 50000,
    total: 100000,
    receivedQty: 1,
    invoicedQty: 0,
    remainingQty: 1
  }, {
    id: 'line-2',
    lineNumber: 20,
    itemCode: 'MED-002',
    description: 'Surgical Instruments Set',
    quantity: 4,
    unitPrice: 5000,
    total: 20000,
    receivedQty: 4,
    invoicedQty: 2,
    remainingQty: 2
  }],
  goodsReceipts: [{
    id: 'gr-1',
    grNumber: 'GR-2024-001',
    grDate: '2024-01-10',
    lineNumber: 10,
    quantity: 1,
    status: 'Posted'
  }, {
    id: 'gr-2',
    grNumber: 'GR-2024-002',
    grDate: '2024-01-15',
    lineNumber: 20,
    quantity: 4,
    status: 'Posted'
  }]
}, {
  poNumber: 'PO00856',
  poDate: '2024-01-15',
  supplier: 'Abbott Australasia Pty Ltd',
  totalAmount: 8181.75,
  currency: 'AUD',
  status: 'Fully Received',
  lineItems: [{
    id: 'line-3',
    lineNumber: 10,
    itemCode: 'LAB-101',
    description: 'Laboratory Test Kits - COVID-19',
    quantity: 100,
    unitPrice: 81.8175,
    total: 8181.75,
    receivedQty: 100,
    invoicedQty: 50,
    remainingQty: 50
  }],
  goodsReceipts: [{
    id: 'gr-3',
    grNumber: 'GR-2024-003',
    grDate: '2024-01-20',
    lineNumber: 10,
    quantity: 100,
    status: 'Posted'
  }]
}, {
  poNumber: 'PO00923',
  poDate: '2024-01-20',
  supplier: 'Abbott Australasia Pty Ltd',
  totalAmount: 45500.0,
  currency: 'AUD',
  status: 'Open',
  lineItems: [{
    id: 'line-4',
    lineNumber: 10,
    itemCode: 'PHARM-201',
    description: 'Pharmaceutical Supplies - Antibiotics',
    quantity: 500,
    unitPrice: 45,
    total: 22500,
    receivedQty: 0,
    invoicedQty: 0,
    remainingQty: 500
  }, {
    id: 'line-5',
    lineNumber: 20,
    itemCode: 'PHARM-202',
    description: 'Pain Relief Medication',
    quantity: 300,
    unitPrice: 50,
    total: 15000,
    receivedQty: 0,
    invoicedQty: 0,
    remainingQty: 300
  }, {
    id: 'line-6',
    lineNumber: 30,
    itemCode: 'PHARM-203',
    description: 'Vitamins and Supplements',
    quantity: 200,
    unitPrice: 40,
    total: 8000,
    receivedQty: 0,
    invoicedQty: 0,
    remainingQty: 200
  }],
  goodsReceipts: []
}];
export function POMatchingModal({
  vendorName,
  invoiceTotal,
  invoiceCurrency,
  onApply,
  onClose
}: POMatchingModalProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedPO, setSelectedPO] = useState<PurchaseOrder | null>(null);
  const [selectedLineItem, setSelectedLineItem] = useState<POLineItem | null>(null);
  const [selectedLines, setSelectedLines] = useState<Set<string>>(new Set());
  const [selectedGRs, setSelectedGRs] = useState<Set<string>>(new Set());
  const [matchingType, setMatchingType] = useState<'2-way' | '3-way'>('2-way');
  const [viewingDocketGR, setViewingDocketGR] = useState<string | null>(null);
  // Keyboard shortcuts
  useKeyboardShortcuts({
    shortcuts: [{
      key: 'Escape',
      handler: () => {
        if (selectedLineItem) {
          setSelectedLineItem(null);
        } else if (selectedPO) {
          setSelectedPO(null);
        } else {
          onClose();
        }
      },
      description: 'Go back or close'
    }],
    enabled: true
  });
  const filteredPOs = MOCK_POS.filter(po => po.supplier === vendorName && (po.poNumber.toLowerCase().includes(searchQuery.toLowerCase()) || po.status.toLowerCase().includes(searchQuery.toLowerCase())));
  const handlePOSelect = (po: PurchaseOrder) => {
    setSelectedPO(po);
    setSelectedLineItem(null);
    setSelectedLines(new Set());
    setSelectedGRs(new Set());
    setMatchingType(po.goodsReceipts.length > 0 ? '3-way' : '2-way');
  };
  const handleLineToggle = (lineId: string) => {
    const newSelected = new Set(selectedLines);
    if (newSelected.has(lineId)) {
      newSelected.delete(lineId);
    } else {
      newSelected.add(lineId);
    }
    setSelectedLines(newSelected);
  };
  const handleGRToggle = (grId: string) => {
    const newSelected = new Set(selectedGRs);
    if (newSelected.has(grId)) {
      newSelected.delete(grId);
    } else {
      newSelected.add(grId);
      // Auto-select the parent line item when a GR is selected
      if (selectedLineItem && selectedPO) {
        const lineId = selectedPO.lineItems.find(line => line.lineNumber === selectedLineItem.lineNumber)?.id;
        if (lineId && !selectedLines.has(lineId)) {
          const newSelectedLines = new Set(selectedLines);
          newSelectedLines.add(lineId);
          setSelectedLines(newSelectedLines);
        }
      }
    }
    setSelectedGRs(newSelected);
  };
  const handleApply = () => {
    if (!selectedPO || selectedLines.size === 0) return;
    onApply(selectedPO.poNumber, Array.from(selectedLines), matchingType);
  };
  const selectedTotal = selectedPO ? selectedPO.lineItems.filter(line => selectedLines.has(line.id)).reduce((sum, line) => sum + line.total, 0) : 0;
  const matchPercentage = invoiceTotal > 0 ? selectedTotal / invoiceTotal * 100 : 0;
  return <DraggableModal isOpen={true} onClose={onClose} title="Match to Purchase Order" defaultWidth={1400} defaultHeight={600} minWidth={1200} minHeight={500} data-id="element-1690">
      <div className="flex flex-col h-full" data-id="element-1691">
        {/* Header with context */}
        <div className="px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-blue-50 to-indigo-50 flex-shrink-0" data-id="element-1692">
          <div className="flex items-start justify-between" data-id="element-1693">
            <div data-id="element-1694">
              <h3 className="text-sm font-semibold text-gray-900 mb-1" data-id="element-1695">
                {vendorName}
              </h3>
              <div className="flex items-center gap-4 text-xs text-gray-600" data-id="element-1696">
                <span data-id="element-1697">
                  Invoice Total:{' '}
                  <span className="font-semibold text-gray-900" data-id="element-1698">
                    {formatAmount(invoiceTotal, invoiceCurrency)}
                  </span>
                </span>
                {selectedLines.size > 0 && <>
                    <span className="text-gray-300" data-id="element-1699">•</span>
                    <span data-id="element-1700">
                      Selected:{' '}
                      <span className="font-semibold text-blue-700" data-id="element-1701">
                        {formatAmount(selectedTotal, invoiceCurrency)}
                      </span>
                    </span>
                    <span className="text-gray-300" data-id="element-1702">•</span>
                    <span className={`font-semibold ${matchPercentage >= 95 && matchPercentage <= 105 ? 'text-green-700' : matchPercentage < 95 ? 'text-amber-700' : 'text-red-700'}`} data-id="element-1703">
                      {matchPercentage.toFixed(1)}% match
                    </span>
                  </>}
              </div>
            </div>
          </div>
        </div>

        {/* 3-Column Layout */}
        <div className="flex-1 flex overflow-hidden" data-id="element-1704">
          {/* Column 1: PO List */}
          <div className={`border-r border-gray-200 bg-white flex flex-col transition-all duration-300 ${selectedPO ? 'w-80' : 'flex-1'}`} data-id="element-1705">
            {/* Search */}
            <div className="p-4 border-b border-gray-200 flex-shrink-0" data-id="element-1706">
              <div className="relative" data-id="element-1707">
                <SearchIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" data-id="element-1708" />
                <Input type="text" placeholder="Search PO number..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="pl-10" autoFocus={!selectedPO} data-id="element-1709" />
              </div>
            </div>

            {/* PO List */}
            <div className="flex-1 overflow-y-auto" data-id="element-1710">
              {filteredPOs.length === 0 ? <div className="flex flex-col items-center justify-center h-full p-6 text-center" data-id="element-1711">
                  <PackageIcon className="w-12 h-12 text-gray-300 mb-3" data-id="element-1712" />
                  <p className="text-sm text-gray-500" data-id="element-1713">
                    No purchase orders found
                  </p>
                </div> : <div className="p-2 space-y-1" data-id="element-1714">
                  {filteredPOs.map(po => <button key={po.poNumber} onClick={() => handlePOSelect(po)} className={`w-full text-left p-3 rounded-lg transition-all ${selectedPO?.poNumber === po.poNumber ? 'bg-blue-100 border-2 border-blue-500 shadow-sm' : 'border border-gray-200 hover:border-blue-300 hover:bg-blue-50'}`} data-id="element-1715">
                      <div className="flex items-start justify-between mb-2" data-id="element-1716">
                        <code className="text-sm font-mono font-semibold text-gray-900" data-id="element-1717">
                          {po.poNumber}
                        </code>
                        {selectedPO?.poNumber === po.poNumber && <CheckIcon className="w-4 h-4 text-blue-600 flex-shrink-0" data-id="element-1718" />}
                      </div>
                      <div className="space-y-1.5" data-id="element-1719">
                        <div className="flex items-center gap-2" data-id="element-1720">
                          <span className={`px-2 py-0.5 text-xs font-semibold rounded ${po.status === 'Open' ? 'bg-gray-100 text-gray-700' : po.status === 'Partially Received' ? 'bg-amber-100 text-amber-700' : 'bg-green-100 text-green-700'}`} data-id="element-1721">
                            {po.status}
                          </span>
                          {po.goodsReceipts.length > 0 && <span className="px-2 py-0.5 text-xs font-semibold rounded bg-green-100 text-green-700 flex items-center gap-1" data-id="element-1722">
                              <TruckIcon className="w-3 h-3" data-id="element-1723" />
                              {po.goodsReceipts.length} GR
                              {po.goodsReceipts.length !== 1 ? 's' : ''}
                            </span>}
                        </div>
                        <div className="text-xs text-gray-600" data-id="element-1724">
                          <div data-id="element-1725">{po.poDate}</div>
                          <div className="font-semibold text-gray-900 mt-0.5" data-id="element-1726">
                            {formatAmount(po.totalAmount, po.currency)}
                          </div>
                          <div className="text-gray-500 mt-0.5" data-id="element-1727">
                            {po.lineItems.length} line
                            {po.lineItems.length !== 1 ? 's' : ''}
                          </div>
                        </div>
                      </div>
                    </button>)}
                </div>}
            </div>
          </div>

          {/* Column 2: Line Items */}
          {selectedPO && <div className={`border-r border-gray-200 bg-gray-50 flex flex-col transition-all duration-300 ${selectedLineItem ? 'w-96' : 'flex-1'}`} data-id="element-1728">
              {/* Header */}
              <div className="p-4 border-b border-gray-200 bg-white flex-shrink-0" data-id="element-1729">
                <div className="flex items-center justify-between mb-2" data-id="element-1730">
                  <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-2" data-id="element-1731">
                    <FileTextIcon className="w-4 h-4 text-blue-600" data-id="element-1732" />
                    Line Items
                  </h3>
                  <button onClick={() => {
                setSelectedPO(null);
                setSelectedLineItem(null);
                setSelectedLines(new Set());
              }} className="text-xs text-blue-600 hover:text-blue-800 hover:underline font-medium" data-id="element-1733">
                    Change PO
                  </button>
                </div>
                <div className="text-xs text-gray-600" data-id="element-1734">
                  Select line items to match ({selectedLines.size} selected)
                </div>
              </div>

              {/* Line Items List */}
              <div className="flex-1 overflow-y-auto p-2 space-y-1" data-id="element-1735">
                {selectedPO.lineItems.map((line, index) => {
              const isSelected = selectedLines.has(line.id);
              const hasGR = selectedPO.goodsReceipts.some(gr => gr.lineNumber === line.lineNumber);
              return <button key={line.id} onClick={() => handleLineToggle(line.id)} className={`w-full text-left p-3 border-2 rounded-lg transition-all ${isSelected ? 'border-blue-500 bg-blue-50' : 'border-gray-200 bg-gray-50 hover:border-blue-300'}`} data-id="element-1736">
                      <div className="flex items-start gap-3" data-id="element-1737">
                        <div className="flex-shrink-0 mt-0.5" data-id="element-1738">
                          <div className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-colors ${isSelected ? 'bg-blue-600 border-blue-600' : 'border-gray-300'}`} data-id="element-1739">
                            {isSelected && <CheckIcon className="w-3 h-3 text-white" data-id="element-1740" />}
                          </div>
                        </div>
                        <div className="flex-1 min-w-0" data-id="element-1741">
                          <div className="flex items-center gap-2 mb-1" data-id="element-1742">
                            <code className="text-xs font-mono bg-gray-100 text-gray-800 px-1.5 py-0.5 rounded font-semibold" data-id="element-1743">
                              {index + 1}
                            </code>
                            <code className="text-xs font-mono text-gray-600" data-id="element-1744">
                              {line.itemCode}
                            </code>
                            {hasGR && <TruckIcon className="w-3 h-3 text-green-600" data-id="element-1745" />}
                          </div>
                          <div className="text-xs text-gray-900 font-medium mb-2 line-clamp-2" data-id="element-1746">
                            {line.description}
                          </div>
                          <div className="grid grid-cols-2 gap-2 text-xs" data-id="element-1747">
                            <div data-id="element-1748">
                              <div className="text-gray-500" data-id="element-1749">Ordered</div>
                              <div className="font-semibold text-gray-900" data-id="element-1750">
                                {line.quantity}
                              </div>
                            </div>
                            <div data-id="element-1751">
                              <div className="text-gray-500" data-id="element-1752">Received</div>
                              <div className={`font-semibold ${line.receivedQty === line.quantity ? 'text-green-700' : line.receivedQty > 0 ? 'text-amber-700' : 'text-gray-900'}`} data-id="element-1753">
                                {line.receivedQty}
                              </div>
                            </div>
                            <div data-id="element-1754">
                              <div className="text-gray-500" data-id="element-1755">Invoiced</div>
                              <div className="font-semibold text-gray-900" data-id="element-1756">
                                {line.invoicedQty}
                              </div>
                            </div>
                            <div data-id="element-1757">
                              <div className="text-gray-500" data-id="element-1758">Remaining</div>
                              <div className="font-semibold text-blue-700" data-id="element-1759">
                                {line.remainingQty}
                              </div>
                            </div>
                          </div>
                          <div className="mt-2 pt-2 border-t border-gray-200" data-id="element-1760">
                            <div className="flex items-center justify-between" data-id="element-1761">
                              <span className="text-gray-500" data-id="element-1762">Total</span>
                              <span className="font-semibold text-gray-900" data-id="element-1763">
                                {formatAmount(line.total, selectedPO.currency)}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* View GRs button */}
                      {hasGR && <div className="px-3 pb-3" data-id="element-1764">
                          <button onClick={e => {
                    e.stopPropagation();
                    setSelectedLineItem(line);
                  }} className="w-full flex items-center justify-between px-3 py-2 text-xs font-medium text-green-700 bg-green-50 hover:bg-green-100 rounded border border-green-200 transition-colors" data-id="element-1765">
                            <span className="flex items-center gap-1.5" data-id="element-1766">
                              <TruckIcon className="w-3.5 h-3.5" data-id="element-1767" />
                              View Goods Receipts
                            </span>
                            <ChevronRightIcon className="w-3.5 h-3.5" data-id="element-1768" />
                          </button>
                        </div>}
                    </button>;
            })}
              </div>
            </div>}

          {/* Column 3: Goods Receipts */}
          {selectedPO && selectedLineItem && <div className="w-80 bg-white flex flex-col" data-id="element-1769">
              {/* Header */}
              <div className="p-4 border-b border-gray-200 flex-shrink-0" data-id="element-1770">
                <div className="flex items-center justify-between mb-2" data-id="element-1771">
                  <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-2" data-id="element-1772">
                    <TruckIcon className="w-4 h-4 text-green-600" data-id="element-1773" />
                    Goods Receipts
                  </h3>
                  <button onClick={() => setSelectedLineItem(null)} className="p-1 hover:bg-gray-100 rounded transition-colors" data-id="element-1774">
                    <XIcon className="w-4 h-4 text-gray-600" data-id="element-1775" />
                  </button>
                </div>
                <div className="text-xs text-gray-600" data-id="element-1776">
                  Line {selectedLineItem.lineNumber} •{' '}
                  {selectedLineItem.itemCode}
                  {selectedGRs.size > 0 && <span className="ml-2 text-green-700 font-semibold" data-id="element-1777">
                      ({selectedGRs.size} selected)
                    </span>}
                </div>
              </div>

              {/* GR List */}
              <div className="flex-1 overflow-y-auto p-4 space-y-2" data-id="element-1778">
                {selectedPO.goodsReceipts.filter(gr => gr.lineNumber === selectedLineItem.lineNumber).map(gr => {
              const isSelected = selectedGRs.has(gr.id);
              return <button key={gr.id} onClick={() => handleGRToggle(gr.id)} className={`w-full text-left p-3 border-2 rounded-lg transition-all ${isSelected ? 'border-green-500 bg-green-50' : 'border-gray-200 bg-gray-50 hover:border-green-300'}`} data-id="element-1779">
                        <div className="flex items-start gap-3" data-id="element-1780">
                          <div className="flex-shrink-0 mt-0.5" data-id="element-1781">
                            <div className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-colors ${isSelected ? 'bg-green-600 border-green-600' : 'border-gray-300'}`} data-id="element-1782">
                              {isSelected && <CheckIcon className="w-3 h-3 text-white" data-id="element-1783" />}
                            </div>
                          </div>
                          <div className="flex-1 min-w-0" data-id="element-1784">
                            <div className="flex items-center justify-between mb-2" data-id="element-1785">
                              <code className="text-xs font-mono font-semibold text-gray-900" data-id="element-1786">
                                {gr.grNumber}
                              </code>
                              <span className={`px-2 py-0.5 text-xs font-semibold rounded ${gr.status === 'Posted' ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}`} data-id="element-1787">
                                {gr.status}
                              </span>
                            </div>
                            <div className="space-y-1 text-xs" data-id="element-1788">
                              <div className="flex justify-between" data-id="element-1789">
                                <span className="text-gray-600" data-id="element-1790">GR Date</span>
                                <span className="font-semibold text-gray-900" data-id="element-1791">
                                  {gr.grDate}
                                </span>
                              </div>
                              <div className="flex justify-between" data-id="element-1792">
                                <span className="text-gray-600" data-id="element-1793">Quantity</span>
                                <span className="font-semibold text-gray-900" data-id="element-1794">
                                  {gr.quantity}
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>
                        <button onClick={e => {
                  e.stopPropagation();
                  setViewingDocketGR(gr.id);
                }} className="flex items-center gap-1.5 px-2 py-1 text-xs font-medium text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded transition-colors" data-id="element-1795">
                          <ImageIcon className="w-3.5 h-3.5" data-id="element-1796" />
                          View Docket
                        </button>
                      </button>;
            })}
              </div>
            </div>}

          {/* Empty state when no PO selected */}
          {!selectedPO && <div className="flex-1 flex items-center justify-center bg-gray-50" data-id="element-1797">
              <div className="text-center max-w-md px-6" data-id="element-1798">
                <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-1799">
                  <PackageIcon className="w-8 h-8 text-blue-600" data-id="element-1800" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2" data-id="element-1801">
                  Select a Purchase Order
                </h3>
                <p className="text-sm text-gray-600 mb-4" data-id="element-1802">
                  Choose a PO from the list to view its line items and match
                  them to your invoice.
                </p>
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-left" data-id="element-1803">
                  <div className="flex items-start gap-3" data-id="element-1804">
                    <AlertCircleIcon className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" data-id="element-1805" />
                    <div className="text-xs text-blue-900" data-id="element-1806">
                      <div className="font-semibold mb-1" data-id="element-1807">
                        Progressive Matching
                      </div>
                      <div className="text-blue-700" data-id="element-1808">
                        Select a PO → Choose line items → View goods receipts
                        (if available) → Apply matching
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 bg-white flex items-center justify-between flex-shrink-0" data-id="element-1809">
          <div className="flex items-center gap-4" data-id="element-1810">
            {selectedPO && selectedLines.size > 0 && <>
                <div className="text-sm" data-id="element-1811">
                  <span className="text-gray-600" data-id="element-1812">Selected Total: </span>
                  <span className="font-semibold text-gray-900" data-id="element-1813">
                    {formatAmount(selectedTotal, selectedPO.currency)}
                  </span>
                </div>
                <div className="w-px h-6 bg-gray-300" data-id="element-1814" />
                <div className="text-sm" data-id="element-1815">
                  <span className="text-gray-600" data-id="element-1816">Match: </span>
                  <span className={`font-semibold ${matchPercentage >= 95 && matchPercentage <= 105 ? 'text-green-700' : matchPercentage < 95 ? 'text-amber-700' : 'text-red-700'}`} data-id="element-1817">
                    {matchPercentage.toFixed(1)}%
                  </span>
                </div>
              </>}
          </div>
          <div className="flex items-center gap-2" data-id="element-1818">
            <Button variant="primary" onClick={handleApply} disabled={!selectedPO || selectedLines.size === 0} data-id="element-1819">
              Apply Matching ({selectedLines.size} line
              {selectedLines.size !== 1 ? 's' : ''})
            </Button>
            <Button variant="ghost" onClick={onClose} data-id="element-1820">
              Cancel
            </Button>
          </div>
        </div>
      </div>

      {/* Delivery Docket Image Viewer */}
      {viewingDocketGR && <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60]" data-id="element-1821">
          <div className="bg-white rounded-lg shadow-2xl w-[800px] max-h-[90vh] flex flex-col" data-id="element-1822">
            {/* Header */}
            <div className="p-4 border-b border-gray-200 flex items-center justify-between" data-id="element-1823">
              <div className="flex items-center gap-2" data-id="element-1824">
                <ImageIcon className="w-5 h-5 text-blue-600" data-id="element-1825" />
                <h3 className="text-lg font-semibold text-gray-900" data-id="element-1826">
                  Delivery Docket
                </h3>
                <code className="text-sm font-mono bg-gray-100 text-gray-800 px-2 py-1 rounded" data-id="element-1827">
                  {selectedPO?.goodsReceipts.find(gr => gr.id === viewingDocketGR)?.grNumber}
                </code>
              </div>
              <button onClick={() => setViewingDocketGR(null)} className="p-1 hover:bg-gray-100 rounded transition-colors" data-id="element-1828">
                <XIcon className="w-5 h-5 text-gray-600" data-id="element-1829" />
              </button>
            </div>

            {/* Image Content */}
            <div className="flex-1 overflow-auto p-6 bg-gray-50" data-id="element-1830">
              <div className="bg-white border-2 border-gray-200 rounded-lg p-8 shadow-sm" data-id="element-1831">
                {/* Mock Delivery Docket */}
                <div className="space-y-6" data-id="element-1832">
                  <div className="text-center border-b-2 border-gray-300 pb-4" data-id="element-1833">
                    <h2 className="text-2xl font-bold text-gray-900" data-id="element-1834">
                      DELIVERY DOCKET
                    </h2>
                    <p className="text-sm text-gray-600 mt-1" data-id="element-1835">
                      Goods Receipt Confirmation
                    </p>
                  </div>

                  <div className="grid grid-cols-2 gap-6" data-id="element-1836">
                    <div data-id="element-1837">
                      <h3 className="text-xs font-semibold text-gray-600 uppercase mb-2" data-id="element-1838">
                        Supplier
                      </h3>
                      <p className="text-sm font-medium text-gray-900" data-id="element-1839">
                        {selectedPO?.supplier}
                      </p>
                    </div>
                    <div data-id="element-1840">
                      <h3 className="text-xs font-semibold text-gray-600 uppercase mb-2" data-id="element-1841">
                        GR Number
                      </h3>
                      <p className="text-sm font-mono font-medium text-gray-900" data-id="element-1842">
                        {selectedPO?.goodsReceipts.find(gr => gr.id === viewingDocketGR)?.grNumber}
                      </p>
                    </div>
                    <div data-id="element-1843">
                      <h3 className="text-xs font-semibold text-gray-600 uppercase mb-2" data-id="element-1844">
                        PO Number
                      </h3>
                      <p className="text-sm font-mono font-medium text-gray-900" data-id="element-1845">
                        {selectedPO?.poNumber}
                      </p>
                    </div>
                    <div data-id="element-1846">
                      <h3 className="text-xs font-semibold text-gray-600 uppercase mb-2" data-id="element-1847">
                        Receipt Date
                      </h3>
                      <p className="text-sm font-medium text-gray-900" data-id="element-1848">
                        {selectedPO?.goodsReceipts.find(gr => gr.id === viewingDocketGR)?.grDate}
                      </p>
                    </div>
                  </div>

                  <div className="border-t-2 border-gray-300 pt-4" data-id="element-1849">
                    <h3 className="text-xs font-semibold text-gray-600 uppercase mb-3" data-id="element-1850">
                      Items Received
                    </h3>
                    <table className="w-full text-sm" data-id="element-1851">
                      <thead className="bg-gray-100" data-id="element-1852">
                        <tr data-id="element-1853">
                          <th className="px-3 py-2 text-left text-xs font-semibold text-gray-700" data-id="element-1854">
                            Item Code
                          </th>
                          <th className="px-3 py-2 text-left text-xs font-semibold text-gray-700" data-id="element-1855">
                            Description
                          </th>
                          <th className="px-3 py-2 text-right text-xs font-semibold text-gray-700" data-id="element-1856">
                            Quantity
                          </th>
                        </tr>
                      </thead>
                      <tbody data-id="element-1857">
                        {selectedPO?.lineItems.filter(line => line.lineNumber === selectedPO?.goodsReceipts.find(gr => gr.id === viewingDocketGR)?.lineNumber).map(line => <tr key={line.id} className="border-b border-gray-200" data-id="element-1858">
                              <td className="px-3 py-2 font-mono text-gray-900" data-id="element-1859">
                                {line.itemCode}
                              </td>
                              <td className="px-3 py-2 text-gray-900" data-id="element-1860">
                                {line.description}
                              </td>
                              <td className="px-3 py-2 text-right font-semibold text-gray-900" data-id="element-1861">
                                {selectedPO?.goodsReceipts.find(gr => gr.id === viewingDocketGR)?.quantity}
                              </td>
                            </tr>)}
                      </tbody>
                    </table>
                  </div>

                  <div className="border-t-2 border-gray-300 pt-4 grid grid-cols-2 gap-6" data-id="element-1862">
                    <div data-id="element-1863">
                      <h3 className="text-xs font-semibold text-gray-600 uppercase mb-2" data-id="element-1864">
                        Received By
                      </h3>
                      <p className="text-sm font-medium text-gray-900" data-id="element-1865">
                        John Smith
                      </p>
                      <p className="text-xs text-gray-600" data-id="element-1866">Warehouse Manager</p>
                    </div>
                    <div data-id="element-1867">
                      <h3 className="text-xs font-semibold text-gray-600 uppercase mb-2" data-id="element-1868">
                        Status
                      </h3>
                      <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold bg-green-100 text-green-700" data-id="element-1869">
                        {selectedPO?.goodsReceipts.find(gr => gr.id === viewingDocketGR)?.status}
                      </span>
                    </div>
                  </div>

                  <div className="border-t-2 border-gray-300 pt-4" data-id="element-1870">
                    <p className="text-xs text-gray-500 italic" data-id="element-1871">
                      This is a system-generated delivery docket. All items have
                      been inspected and verified upon receipt.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="p-4 border-t border-gray-200 flex justify-end" data-id="element-1872">
              <button onClick={() => setViewingDocketGR(null)} className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded transition-colors" data-id="element-1873">
                Close
              </button>
            </div>
          </div>
        </div>}
    </DraggableModal>;
}